import React, { useState, useEffect } from 'react'
import { useT } from '../../hooks/useTranslation'
import { getExchanges, connectExchange, disconnectExchange } from '../../services/api'
import { useDashboardStore } from '../../store/dashboardStore'
// Биржа бота и признак виртуального бота — из общего модуля слотов: блокировка
// правки ключей считается по бирже строки (демо-бот на Bybit не мешает менять
// ключи Binance).
import { hasBotOnExchange } from '../../utils/botSlots'
// Режим биржи из конфига админки: вкл / «в разработке» / скрыта (2026-09-28).
import { exchangeModeOf } from '../../utils/exchangeMode'
// Логотипы Binance и Bitget (2026-09-20, task.txt) — ЛОКАЛЬНЫЕ файлы, а не
// внешние URL: CoinGecko отдавал по ним 403 (hotlink block), CoinMarketCap
// работал через curl, но в webview Telegram картинки всё равно не грузились,
// и оставались цветные квадраты. С локальным ассетом картинка приходит с того
// же origin, что и само приложение, — блокировать её нечему. Vite бандлит
// файлы из ./logos в сборку (base './' → относительные пути).
//
// ⚠ ВАЖНО (2026-09-20, второй заход). У Bitget картинка появилась, а у Binance
// нет — и причин было ДВЕ, обе устранены в файле binance.png:
//  1) доставка. Vite вшивает ассеты ≤4096 Б в JS как base64 data-URI, и именно
//     вшитый логотип Binance (было 3691 Б) не отрисовался в webview Telegram,
//     а выложенный отдельным файлом логотип Bitget (7547 Б) — отрисовался.
//  2) прозрачность. Официальный ассет Binance — жёлтый ромб rgb(243,186,47) на
//     ПРОЗРАЧНОМ фоне (65.6% пикселей), а подложка квадрата — жёлтая tagBg
//     #F0B90B. Ромб ложился на почти такой же жёлтый квадрат, и карточка
//     выглядела как ровный жёлтый квадрат, то есть «картинки нет». У рабочих
//     логотипов фон свой и непрозрачный: Bybit — тёмный rgb(35,38,53), Bitget —
//     циан rgb(0,224,255).
// Что сделано с binance.png: перекодирован из палитрового PNG (color type 3 +
// tRNS) в обычный RGBA (color type 6), как у bitget.png, и прозрачность
// залита непрозрачным чёрным — получился 200×200 RGBA, 66.4% чёрного фона и
// 23.9% жёлтого ромба по центру (чёрный — канонический вид знака Binance и
// он же контрастен жёлтому). Итог: файл 4301 Б > порога 4096 → Vite выкладывает
// его отдельным файлом assets/*.png и ссылается через new URL(...,
// import.meta.url), ровно как на bitget.png. Ниже порога 4096 файл не
// уменьшать и обратно в палитровый формат не переводить; на всякий случай в
// vite.config.js выставлен assetsInlineLimit: 0 (см. комментарий там).
import binanceLogo from './logos/binance.png'
import bitgetLogo from './logos/bitget.png'

const BYBIT_LOGO = 'https://coin-images.coingecko.com/markets/images/698/small/bybit_spot.png?1706864649'
const KUCOIN_LOGO = 'https://coin-images.coingecko.com/markets/images/311/small/kucoin.jpg?1698034649'
const BINGX_LOGO = 'https://s2.coinmarketcap.com/static/img/exchanges/64x64/2703.png'

const EXCHANGES = [
  { id: 'bybit', name: 'Bybit', tag: 'BB', tagColor: '#fff', tagBg: '#F7A600', logo: BYBIT_LOGO, available: true },
  // Binance подключена (2026-09-17, task.txt): ключи на биржу, боты, фандинг,
  // планировщик — та же схема, что Bybit.
  { id: 'binance', name: 'Binance', tag: 'BN', tagColor: '#fff', tagBg: '#F0B90B', logo: binanceLogo, available: true },
  // Bitget подключена (2026-09-17, task.txt п.3): та же схема, что Bybit,
  // но подпись API v2 требует парольную фразу (needsPassphrase) — поле
  // появляется только у этой биржи (см. ExchangeRow).
  { id: 'bitget', name: 'Bitget', tag: 'BG', tagColor: '#fff', tagBg: '#00C087', logo: bitgetLogo, available: true, needsPassphrase: true },
  { id: 'kucoin', name: 'KuCoin', tag: 'KC', tagColor: '#fff', tagBg: '#24AE8F', logo: KUCOIN_LOGO, available: false },
  { id: 'bingx', name: 'BingX', tag: 'BX', tagColor: '#fff', tagBg: '#2E6BF6', logo: BINGX_LOGO, available: false },
]

const DEBUG_MODE = typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('debug') === '1'

// Квадрат 42×42 с логотипом биржи (рядом с названием в списке).
// Картинка лежит ПОВЕРХ аббревиатуры, а не вместо неё: если логотип почему-то
// не загрузится, onError прячет только картинку, и остаётся фирменный цветной
// квадрат с «BN»/«BG» — это выглядит как задумка, а не как пропавшая картинка.
// Раньше аббревиатура не рендерилась вовсе (условие ex.logo ? <img> : ex.tag),
// поэтому любая осечка с загрузкой давала пустой цветной квадрат — ровно то,
// на что жаловался владелец 2026-09-20.
function ExchangeLogo({ ex, bg, color }) {
  return (
    <div style={{
      position: 'relative',
      width: 42, height: 42, borderRadius: 12,
      background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 800, color, flexShrink: 0,
      overflow: 'hidden',
    }}>
      {ex.tag}
      {ex.logo && (
        <img
          src={ex.logo}
          alt={ex.name}
          style={{ position: 'absolute', top: 0, left: 0, width: 42, height: 42, objectFit: 'cover' }}
          onError={e => { e.target.style.display = 'none' }}
        />
      )}
    </div>
  )
}

function ExchangeRow({ ex, connected, maskedKey, keyMasks, devLocked, onConnect, onDisconnect, hasCreatedBot, hasAccess, onNoAccessClick, canEditReal, canEditDemo, t }) {
  // Открыть гайд по API-ключам (модалка activeModal === 'api-keys', см. App.jsx).
  const setActiveModal = useDashboardStore(s => s.setActiveModal)
  const [open, setOpen] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [apiSecret, setApiSecret] = useState('')
  const [demoKey, setDemoKey] = useState('')
  const [demoSecret, setDemoSecret] = useState('')
  // Парольная фраза Bitget (2026-09-17, task.txt п.3): поле только для этой
  // биржи. При «Изменить» показываем маску; если юзер её не трогал — не
  // отправляем (бэкенд сохранит прежнюю фразу).
  const [passphrase, setPassphrase] = useState('')
  const [saving, setSaving] = useState(false)
  const [connectError, setConnectError] = useState('')
  const [showTooltip, setShowTooltip] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [showBlockHint, setShowBlockHint] = useState(false)

  const needsPassphrase = !!ex.needsPassphrase
  const hasRealKeys = apiKey.trim() !== '' && apiSecret.trim() !== ''
  const hasDemoKeys = demoKey.trim() !== '' && demoSecret.trim() !== ''
  // Для Bitget connect без фразы бессмыслен (бэкенд вернёт 400) — блокируем
  // отправку, пока не введена фраза (или при «Изменить» не осталась маска).
  // Введённая маска/пустая длина → невалидно; непустая — обязана быть 8–64.
  const passphraseOk =
    !needsPassphrase ||
    (!passphrase.trim() && editMode && keyMasks.passphraseMask) ||
    (passphrase.length >= 8 && passphrase.length <= 64)
  // Каждый тип ключей доступен только по своей подписке: реальные — при
  // реальной, демо — при любой активной. Строка «недоступного» типа не
  // участвует в отправке (инпуты заблокированы).
  const canSubmit = ((hasRealKeys && canEditReal) || (hasDemoKeys && canEditDemo)) && hasAccess && passphraseOk

  // При «Изменить» поля подставляем масками. Поле, которое пользователь не
  // трогал (осталось маской), НЕ отправляем на бэкенд — иначе маска
  // перезапишет сохранённый ключ (а по формату маска и не пройдёт).
  const unchangedMask = (val, mask) => editMode && mask && val === mask

  const handleConfirm = async () => {
    if (!canSubmit) return
    setSaving(true)
    setConnectError('')
    try {
      // Поля, которые остались масками («не трогал») — отправляем пустыми,
      // чтобы бэкенд сохранил прежний ключ, а не записал маску.
      // .trim() — при копировании ключа из веб-кабинета биржи в буфер часто
      // попадает пробел или перевод строки. Самого ключа этот хвост не касается,
      // но формат на сервере ломает (400 «Invalid format»), поэтому чистим здесь.
      const send = (val, mask) => {
        const v = val.trim()
        return unchangedMask(v, mask) ? '' : v
      }
      await onConnect(
        ex.id,
        send(apiKey, keyMasks.realKeyMask),
        send(apiSecret, keyMasks.realSecretMask),
        send(demoKey, keyMasks.demoKeyMask),
        send(demoSecret, keyMasks.demoSecretMask),
        // Фраза: пустая маска (не меняли) → '' → бэкенд сохранит прежнюю.
        send(passphrase, keyMasks.passphraseMask),
      )
      setApiKey('')
      setApiSecret('')
      setDemoKey('')
      setDemoSecret('')
      setPassphrase('')
      setOpen(false)
      setEditMode(false)
    } catch (err) {
      setConnectError(err?.message ?? t.connectionError)
    } finally {
      setSaving(false)
    }
  }

  const doDelete = async () => {
    setSaving(true)
    setConnectError('')
    try {
      await onDisconnect(ex.id)
      setApiKey('')
      setApiSecret('')
      setDemoKey('')
      setDemoSecret('')
      setPassphrase('')
      setEditMode(false)

      const tg = window.Telegram?.WebApp
      if (tg?.showAlert) tg.showAlert(t.keysDeleted)
    } catch (err) {
      const message = err?.message ?? t.deleteError
      setConnectError(message)
      const tg = window.Telegram?.WebApp
      if (tg?.showAlert) tg.showAlert(`❌ ${message}`)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = () => {
    const tg = window.Telegram?.WebApp
    const confirmMessage = t.confirmDeleteKeys

    if (tg?.showConfirm) {
      // showConfirm в Telegram WebApp принимает колбэк (confirmed) => {}
      tg.showConfirm(confirmMessage, (confirmed) => {
        if (confirmed) doDelete()
      })
    } else if (window.confirm(confirmMessage)) {
      // fallback для окружений без Telegram WebApp (например, тестирование в браузере)
      doDelete()
    }
  }

  const toggleOpen = () => {
    if (!hasAccess && !open) {
      onNoAccessClick()
      return
    }
    setOpen(prev => {
      if (prev) {
        setApiKey('')
        setApiSecret('')
        setDemoKey('')
        setDemoSecret('')
        setPassphrase('')
        setConnectError('')
      }
      return !prev
    })
    setEditMode(false)
  }

  const inputStyle = {
    width: '100%', padding: '11px 14px',
    background: '#0A0A0A',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10, color: '#fff', fontSize: 14, outline: 'none',
    boxSizing: 'border-box',
    WebkitTextFillColor: '#fff',
    transition: 'background-color 5000s ease-in-out 0s',
  }

  const labelStyle = {
    fontSize: 12, color: '#8E8E93', marginBottom: 6, marginTop: 8
  }

  // Карточка недоступна: либо биржа ещё не подключена в коде приложения
  // (ex.available === false), либо админ перевёл её в режим «в разработке»
  // (devLocked) — вид у обеих одинаковый, и он же просил владелец.
  if (!ex.available || devLocked) {
    return (
      <div
        style={{ marginBottom: 10, position: 'relative' }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 14px', background: '#1A1A1A',
          borderRadius: 14,
          border: '1px solid rgba(255,255,255,0.04)',
          opacity: 0.5,
          filter: 'grayscale(100%)',
          cursor: 'not-allowed',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ExchangeLogo ex={ex} bg="#3A3A3A" color="#636366" />
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#636366' }}>{ex.name}</div>
              <div style={{ fontSize: 12, marginTop: 1, color: '#636366' }}>{t.inDevelopment}</div>
            </div>
          </div>
          <span style={{ fontSize: 12, color: '#636366', fontStyle: 'italic' }}>{t.soon}</span>
        </div>

        {showTooltip && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 6,
            background: '#1C1C1E', border: '1px solid rgba(255,107,53,0.25)',
            borderRadius: 10, padding: '10px 14px', zIndex: 100,
            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
          }}>
            <p style={{ fontSize: 12, color: '#8E8E93', margin: 0, lineHeight: 1.5 }}>
              {t.integrationInDev.replaceAll('{ex}', ex.name)}
            </p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 14px', background: '#1A1A1A',
        borderRadius: open || editMode ? '14px 14px 0 0' : 14,
        border: '1px solid rgba(255,255,255,0.04)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ExchangeLogo ex={ex} bg={ex.tagBg} color={ex.tagColor} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{ex.name}</div>
            <div style={{
              fontSize: 12,
              marginTop: 2,
              color: connected ? '#34D399' : '#636366',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              {connected && (
                <span style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: '#34D399',
                  display: 'block',
                  boxShadow: '0 0 4px rgba(52, 199, 89, 0.4)'
                }} />
              )}
              {!connected && <span>{t.notConnected}</span>}
            </div>
          </div>
        </div>

        {connected ? (
          <div style={{ display: 'flex', gap: 8, position: 'relative' }}>
            {/* Кнопка Изменить */}
            <div 
              style={{ position: 'relative' }}
              onMouseEnter={() => hasCreatedBot && setShowBlockHint('edit')}
              onMouseLeave={() => setShowBlockHint(null)}
            >
              <button
                onClick={() => {
                  if (hasCreatedBot) return;
                  console.log('DEBUG keyMasks:', keyMasks);
                  // Заполняем поля масками, если ключи сохранены
                  setApiKey(keyMasks?.realKeyMask || '')
                  setApiSecret(keyMasks?.realSecretMask || '')
                  setDemoKey(keyMasks?.demoKeyMask || '')
                  setDemoSecret(keyMasks?.demoSecretMask || '')
                  setPassphrase(keyMasks?.passphraseMask || '')
                  setConnectError('')
                  setEditMode(true)
                  setOpen(false)
                }}
                disabled={hasCreatedBot}
                style={{
                  padding: '10px 14px', minHeight: 44, borderRadius: 10,
                  fontSize: 13, fontWeight: 600,
                  background: hasCreatedBot ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.08)',
                  color: hasCreatedBot ? '#48484A' : '#8E8E93',
                  border: 'none', cursor: hasCreatedBot ? 'not-allowed' : 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                {t.edit}
              </button>
              {showBlockHint === 'edit' && (
                <div style={{
                  position: 'absolute', bottom: '120%', left: '50%', transform: 'translateX(-50%)',
                  background: '#1C1C1E', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8, padding: '8px 12px', zIndex: 100,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)', width: 'max-content', maxWidth: '200px'
                }}>
                  <p style={{ fontSize: 11, color: '#8E8E93', margin: 0, textAlign: 'center' }}>
                    {t.deleteBotToEditKeys}
                  </p>
                </div>
              )}
            </div>

            {/* Кнопка Удалить */}
            <div 
              style={{ position: 'relative' }}
              onMouseEnter={() => hasCreatedBot && setShowBlockHint('delete')}
              onMouseLeave={() => setShowBlockHint(null)}
            >
              <button
                onClick={() => {
                  if (hasCreatedBot || saving) return;
                  handleDelete();
                }}
                disabled={hasCreatedBot || saving}
                style={{
                  padding: '10px 14px', minHeight: 44, borderRadius: 10,
                  fontSize: 13, fontWeight: 600,
                  background: hasCreatedBot ? 'rgba(255,59,48,0.03)' : 'rgba(255,59,48,0.15)',
                  color: hasCreatedBot ? '#48484A' : '#FF3B30',
                  border: 'none', cursor: hasCreatedBot ? 'not-allowed' : 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? '...' : t.delete}
              </button>
              {showBlockHint === 'delete' && (
                <div style={{
                  position: 'absolute', bottom: '120%', left: '50%', transform: 'translateX(-50%)',
                  background: '#1C1C1E', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8, padding: '8px 12px', zIndex: 100,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)', width: 'max-content', maxWidth: '200px'
                }}>
                  <p style={{ fontSize: 11, color: '#8E8E93', margin: 0, textAlign: 'center' }}>
                    {t.deleteBotToEditKeys}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <button
            onClick={toggleOpen}
            disabled={!hasAccess}
            data-hint="exchange-connect"
            style={{
              padding: '10px 16px', minHeight: 44, borderRadius: 10,
              fontSize: 13, fontWeight: 600,
              background: hasAccess
                ? 'linear-gradient(135deg, #10B981, #059669)'
                : 'rgba(255,255,255,0.06)',
              color: hasAccess ? '#fff' : '#636366',
              border: 'none',
              cursor: hasAccess ? 'pointer' : 'not-allowed',
              opacity: hasAccess ? 1 : 0.6,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {open ? t.collapse : t.connect}
          </button>
        )}
      </div>

      {((open && !connected) || (editMode && connected)) && (
        <div style={{
          background: '#111', borderRadius: editMode ? 14 : '0 0 14px 14px',
          border: '1px solid rgba(255,255,255,0.04)',
          borderTop: editMode ? undefined : 'none',
          padding: '14px', display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <p style={{ fontSize: 12, color: '#636366', margin: 0 }}>
              {editMode ? t.editApiKeys : t.securityNote}
            </p>
            {/* 2026-09-27, task.txt: подпись выше была непонятна новичку —
                рядом с формой даём путь к гайду, где права ключа разобраны
                пошагово (модалка уже существует, открывается из инструкции). */}
            <button
              onClick={() => setActiveModal('api-keys')}
              style={{
                alignSelf: 'flex-start', padding: 0, margin: 0, background: 'none', border: 'none',
                color: '#34D399', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                textAlign: 'left', WebkitTapHighlightColor: 'transparent',
              }}
            >
              {t.keysGuideLink}
            </button>
          </div>

          {/* Длина ключа у бирж РАЗНАЯ: Bybit — 18/36, Binance — 64/64. Поэтому
              у полей ключей СОЗНАТЕЛЬНО нет maxLength (task.txt, 2026-09-28):
              браузер молча обрезал вставленный ключ по старому лимиту Bybit
              (18 символов), и на Binance в базу уходил испорченный ключ, а бот
              потом не мог подключиться. Поле, которое молча режет секрет, —
              это и есть ошибка: если значение слишком длинное, его должен
              отклонить сервер с понятным текстом (см. KEY_PATTERN в api.js),
              а не подрезать ввод. */}
          <div>
            <div style={labelStyle}>{t.realAccountApi}</div>
            <input
              type="password"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder="API Key"
              disabled={!canEditReal}
              style={!canEditReal ? {...inputStyle, opacity: 0.45, cursor: 'not-allowed'} : inputStyle}
            />
            <input
              type="password"
              value={apiSecret}
              onChange={e => setApiSecret(e.target.value)}
              placeholder="API Secret"
              disabled={!canEditReal}
              style={!canEditReal ? {...inputStyle, marginTop: 8, opacity: 0.45, cursor: 'not-allowed'} : {...inputStyle, marginTop: 8}}
            />
            {!canEditReal && (
              <span style={{ display: 'block', fontSize: 11, color: '#8E8E93', marginTop: 6, opacity: 0.85 }}>
                🔒 {t.realKeysLocked}
              </span>
            )}
          </div>

          <div>
            <div style={labelStyle}>{t.demoAccountApi}</div>
            <input
              type="password"
              value={demoKey}
              onChange={e => setDemoKey(e.target.value)}
              placeholder="Demo API Key"
              disabled={!canEditDemo}
              style={!canEditDemo ? {...inputStyle, opacity: 0.45, cursor: 'not-allowed'} : inputStyle}
            />
            <input
              type="password"
              value={demoSecret}
              onChange={e => setDemoSecret(e.target.value)}
              placeholder="Demo API Secret"
              disabled={!canEditDemo}
              style={!canEditDemo ? {...inputStyle, marginTop: 8, opacity: 0.45, cursor: 'not-allowed'} : {...inputStyle, marginTop: 8}}
            />
            {!canEditDemo && (
              <span style={{ display: 'block', fontSize: 11, color: '#8E8E93', marginTop: 6, opacity: 0.85 }}>
                🔒 {t.demoKeysLocked}
              </span>
            )}
          </div>

          {/* Passphrase (2026-09-17, task.txt п.3): требуется только Bitget —
              API v2 подписывает запросы фразой (ACCESS-PASSPHRASE). Без неё
              ключи бэкенд не сохранит (400). Маскируется как ключи. */}
          {needsPassphrase && (
            <div>
              <div style={labelStyle}>{t.bitgetPassphrase}</div>
              <input
                type="password"
                value={passphrase}
                onChange={e => setPassphrase(e.target.value)}
                placeholder={editMode && keyMasks.passphraseMask ? t.bitgetPassphrasePlaceholder : 'Passphrase (8–64 симв.)'}
                style={{
                  ...inputStyle,
                  border: (passphrase && (passphrase.length < 8 || passphrase.length > 64))
                    ? '1px solid rgba(255,59,48,0.5)'
                    : '1px solid rgba(255,255,255,0.08)',
                }}
              />
              <span style={{ display: 'block', fontSize: 11, color: '#8E8E93', marginTop: 6, lineHeight: 1.4 }}>
                🗝️ {t.bitgetPassphraseHint}
              </span>
            </div>
          )}

          <button
            onClick={handleConfirm}
            disabled={!canSubmit}
            style={{
              padding: '12px', borderRadius: 12, fontSize: 15, fontWeight: 600,
              background: canSubmit
                ? 'linear-gradient(135deg, #10B981, #059669)'
                : 'rgba(255,255,255,0.06)',
              color: canSubmit ? '#fff' : '#636366',
              border: 'none', cursor: canSubmit ? 'pointer' : 'default',
              transition: 'all 0.2s',
              WebkitTapHighlightColor: 'transparent',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? '...' : (editMode ? t.saveChanges : t.confirmConnect)}
          </button>

          {connectError && (
            <p style={{ margin: '2px 0 0', fontSize: 11, color: '#FF3B30', textAlign: 'center' }}>
              {connectError}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default function ExchangesPage() {
  const t = useT()
  const userBots = useDashboardStore(s => s.userBots)
  const botsLoaded = useDashboardStore(s => s.botsLoaded)
  const fetchUserBots = useDashboardStore(s => s.fetchUserBots)
  const setHasApiKeys = useDashboardStore(s => s.setHasApiKeys)
  const setPerTypeKeys = useDashboardStore(s => s.setPerTypeKeys)
  // Доступность типов счёта по подписке (из /api/users/me через стор).
  const demoAllowed = useDashboardStore(s => s.demoAllowed)
  const realAllowed = useDashboardStore(s => s.realAllowed)
  // Режим бирж из конфига приложения (админка). Три состояния (task.txt,
  // 2026-09-28): 'off' — биржи нет в списке; 'dev' — карточка остаётся, но не
  // кликабельна и подписана «в разработке»; 'on' — обычная карточка.
  // Подключить можно те, что поддерживает бэкенд (Bybit, Binance, Bitget) —
  // остальные и раньше показывались как «Скоро» (available: false).
  const appConfig = useDashboardStore(s => s.appConfig)

  // ── Список бирж для отображения ──────────────────────────────────────────
  // 'dev' ОСТАЁТСЯ в списке (его блокирует уже сама карточка), 'off' убираем.
  const visibleExchanges = EXCHANGES.filter(ex => exchangeModeOf(appConfig, ex.id) !== 'off')

  const [exchangesStatus, setExchangesStatus] = useState({})
  // Статус ключей пришёл с сервера. До этого `connected` для всех бирж === false,
  // а значит режим 'dev' заблокировал бы карточку и тому, кто уже подключён, —
  // на первом кадре он увидел бы «в разработке» вместо своих ключей.
  const [statusLoaded, setStatusLoaded] = useState(false)
  const [debugInfo, setDebugInfo] = useState(null)

  // Доступ к подключению ключей определяется ПОДПИСКОЙ: без активной подписки
  // любого типа кнопка «Подключить» неактивна (по задаче). Раньше доступ
  // определялся наличием пользователя в БД — из-за этого у нового пользователя
  // без подписки кнопка была кликабельна.
  const hasAccess = demoAllowed || realAllowed

  // Блокируем изменение ключей, если:
  // 1. Список ботов ещё не был успешно загружен с сервера в этой сессии
  //    (botsLoaded === false) — сюда попадает и самый первый рендер сразу
  //    после логина, до ответа сервера.
  // 2. Или у пользователя уже есть НЕвиртуальный бот НА ЭТОЙ БИРЖЕ — её ключи
  //    использует её бот, менять их на ходу нельзя.
  // Раньше условие было общим (`userBots.length > 0`): бот на Bybit запрещал
  // править и удалять ключи Binance и Bitget — то есть подключить ключи новой
  // биржи после создания первого бота было нельзя. Слот бота — пара «тип счёта ×
  // биржа» (2026-09-20), поэтому и блокировка считается по бирже строки.
  const hasCreatedBotOn = exId => !botsLoaded || hasBotOnExchange(userBots, exId)

  // Биржа в режиме «в разработке» (админка → «Система» → «Биржи»): карточка
  // остаётся на месте, но не кликабельна и подписана «в разработке».
  // Тех, кто УЖЕ подключил эту биржу или создал на ней бота, не запираем —
  // иначе они потеряли бы доступ к своим ключам и управлению ботом; ждём
  // загрузки статуса, иначе на первом кадре заблокировали бы и их.
  const isDevLocked = exId =>
    exchangeModeOf(appConfig, exId) === 'dev' &&
    statusLoaded && botsLoaded &&
    !exchangesStatus[exId]?.connected &&
    !hasCreatedBotOn(exId)

  const loadExchangesData = () => {
    getExchanges().then(list => {
      const statusMap = {}
      list.forEach(e => {
        statusMap[e.exchange] = {
          connected: e.connected,
          maskedKey: e.maskedKey,
          realKeyMask: e.realKeyMask || '',
          realSecretMask: e.realSecretMask || '',
          demoKeyMask: e.demoKeyMask || '',
          demoSecretMask: e.demoSecretMask || '',
          passphraseMask: e.passphraseMask || '',
        }
      })
      setExchangesStatus(statusMap)
      setStatusLoaded(true)

      const hasAnyConnected = list.some(e => e.connected)
      setHasApiKeys(hasAnyConnected)
      // По-типовые флаги ключей — для кнопки создания ботов каждого типа.
      setPerTypeKeys({
        hasRealKeys: list.some(e => e.hasRealKeys),
        hasDemoKeys: list.some(e => e.hasDemoKeys),
      })
    }).catch((err) => {
      console.error('Failed to load exchanges keys:', err)
      setHasApiKeys(false)
    })
  }

  // Загружаем актуальный список ботов сразу при входе в раздел "Биржи",
  // не полагаясь на то, что его уже успел подгрузить раздел "Торговля".
  // Пока запрос не завершится, hasCreatedBotOn(...) остаётся true — кнопки
  // изменения/удаления ключей у всех бирж заблокированы.
  useEffect(() => {
    if (!botsLoaded) {
      fetchUserBots()
    }
  }, [botsLoaded, fetchUserBots])

  // Сохранённые ключи и статус подключения грузим ВСЕГДА: статус «Подключено»
  // и маски ключей при «Изменить» не должны зависеть от подписки. Доступ к
  // вводу/сохранению ключей при этом остаётся за подпиской (см. hasAccess).
  useEffect(() => {
    loadExchangesData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleConnect = async (id, apiKey, apiSecret, demoKey, demoSecret, passphrase) => {
    await connectExchange(id, apiKey, apiSecret, demoKey || '', demoSecret || '', passphrase || '')
    loadExchangesData()
  }

  const handleDisconnect = async (id) => {
    await disconnectExchange(id) // если упадёт (напр. 401/500) — ошибка уйдёт наверх в ExchangeRow

    setExchangesStatus(prev => {
      const updated = { ...prev, [id]: { connected: false, maskedKey: null } }

      // Считаем hasAnyConnected по АКТУАЛЬНОМУ (обновлённому) состоянию,
      // а не по устаревшему замыканию из внешнего exchangesStatus
      const hasAnyConnected = Object.values(updated).some(v => v?.connected)
      setHasApiKeys(hasAnyConnected)

      return updated
    })

    // Флаги «есть ключи реального/демо» пересчитываем с сервера: с 2026-09-17
    // удаление ключей ОДНОЙ биржи не обязано стирать счёт другого типа/биржи
    // (у юзера могут остаться Bybit-ключи при удалении Binance-ключей и т.п.).
    getExchanges().then(list => setPerTypeKeys({
      hasRealKeys: list.some(e => e.hasRealKeys),
      hasDemoKeys: list.some(e => e.hasDemoKeys),
    })).catch(() => {})
  }

  const handleNoAccessClick = () => {
    const tg = window.Telegram?.WebApp
    const message = t.subscriptionRequiredConnect
    if (tg?.showAlert) {
      tg.showAlert(message)
    } else {
      alert(message)
    }
  }

  return (
    <section>
      {DEBUG_MODE && (
        <pre style={{
          fontSize: 10, color: '#0f0', background: '#000',
          padding: 10, borderRadius: 8, overflow: 'auto',
          marginBottom: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
        }}>
          {JSON.stringify({ hasAccess, botsLoaded, userBotsCount: userBots.length, debugInfo, exchangesStatus }, null, 2)}
        </pre>
      )}

      <p style={{ fontSize: 13, color: '#636366', marginBottom: 16, lineHeight: 1.5 }}>
        {t.exchangeDesc}
      </p>

      {visibleExchanges.map(ex => (
        <ExchangeRow
          key={ex.id}
          ex={ex}
          connected={!!exchangesStatus[ex.id]?.connected}
          maskedKey={exchangesStatus[ex.id]?.maskedKey}
          keyMasks={{
            realKeyMask: exchangesStatus[ex.id]?.realKeyMask || '',
            realSecretMask: exchangesStatus[ex.id]?.realSecretMask || '',
            demoKeyMask: exchangesStatus[ex.id]?.demoKeyMask || '',
            demoSecretMask: exchangesStatus[ex.id]?.demoSecretMask || '',
            passphraseMask: exchangesStatus[ex.id]?.passphraseMask || '',
          }}
          devLocked={isDevLocked(ex.id)}
          onConnect={handleConnect}
          onDisconnect={handleDisconnect}
          hasCreatedBot={hasCreatedBotOn(ex.id)}
          hasAccess={hasAccess}
          canEditReal={realAllowed}
          canEditDemo={demoAllowed}
          onNoAccessClick={handleNoAccessClick}
          t={t}
        />
      ))}
    </section>
  )
}